import React from "react";
export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="mb-12 space-y-2">
          <h1 className="text-4xl font-bold tracking-tight">Terms of Service</h1>
          <p className="text-muted-foreground">
            Company: To Better Digital (TBD)
          </p>
          <p className="text-muted-foreground">
            Website:{" "}
            <a href="https://to-better-digital.lovable.app" className="text-primary hover:underline">
              https://to-better-digital.lovable.app
            </a>
          </p>
          <p className="text-muted-foreground">
            Contact:{" "}
            <a href="mailto:jeremy@tobetterdigital.com" className="text-primary hover:underline">
              jeremy@tobetterdigital.com
            </a>
          </p>
          <p className="text-sm text-muted-foreground">
            Last updated: March 3, 2026
          </p>
        </div>

        <div className="space-y-10 text-sm leading-relaxed text-muted-foreground">
          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">1. Acceptance of These Terms</h2>
            <p className="mb-3">
              These Terms of Service ("Terms") govern your access to and use of the To Better Digital (TBD) platform and related services (the "Service"). By creating an account, accessing, or using the Service, you agree to these Terms. If you do not agree, do not use the Service.
            </p>
            <p>
              If you use the Service on behalf of a company, agency, or other entity, you represent and warrant that you have authority to bind that entity, and "you" refers to that entity.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">2. The Service</h2>
            <p className="mb-3">
              The Service helps e-commerce brands and agencies automate and manage Meta (Facebook/Instagram) advertising operations, including campaign creation, bulk ad operations, creative asset uploads (including from Google Drive), and reporting using synced performance metrics.
            </p>
            <p>
              Meta and Google services are third-party services. Your use of Meta and Google remains subject to their own terms, policies, and platform rules.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">3. Eligibility and Account Registration</h2>
            <ul className="list-disc space-y-1 pl-6">
              <li>You must be able to form a legally binding contract to use the Service.</li>
              <li>You must provide accurate and complete information and keep it up to date.</li>
              <li>You are responsible for maintaining the confidentiality of your credentials and for all activities under your account.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">4. Integrations and Permissions (Meta and Google Drive)</h2>
            <p className="mb-3">
              To use certain features, you may connect your Meta ad account and optionally Google Drive through OAuth authorization. You are responsible for ensuring you have the necessary rights and permissions to connect the relevant accounts and to use any pages, ad accounts, and creative assets accessed through the integrations.
            </p>
            <p>
              You may revoke access at any time through your Meta or Google settings or by disconnecting the integration within the Service. Revocation may limit or disable related functionality.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">5. Your Content and Data</h2>

            <h3 className="mb-2 font-medium text-foreground">5.1 Your content</h3>
            <p className="mb-4">
              "Customer Content" includes ad names, templates, configurations, text, creative assets you provide or select, and other materials you submit to or process through the Service. You retain ownership of your Customer Content.
            </p>

            <h3 className="mb-2 font-medium text-foreground">5.2 License to operate the Service</h3>
            <p className="mb-4">
              You grant us a limited, non-exclusive, worldwide license to host, process, transmit, and display Customer Content solely as necessary to provide and improve the Service, comply with law, and enforce these Terms.
            </p>

            <h3 className="mb-2 font-medium text-foreground">5.3 Responsibility for Customer Content</h3>
            <p>
              You are solely responsible for Customer Content and represent and warrant that you have all rights necessary to use it and to grant the rights described in these Terms. You also agree that your Customer Content and its use through the Service will not violate any law, regulation, or third-party rights.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">6. Acceptable Use</h2>
            <p className="mb-2">You agree not to:</p>
            <ul className="list-disc space-y-1 pl-6">
              <li>Use the Service in violation of any applicable law, regulation, or platform policies (including Meta and Google policies).</li>
              <li>Upload or use content that infringes intellectual property rights, is unlawful, harmful, deceptive, or abusive.</li>
              <li>Attempt to gain unauthorized access to the Service, accounts, systems, or networks.</li>
              <li>Interfere with or disrupt the integrity or performance of the Service (including by introducing malware or excessive load).</li>
              <li>Reverse engineer, decompile, or attempt to extract source code of the Service except to the extent permitted by law.</li>
              <li>Resell, rent, or provide the Service to third parties as a service bureau without our written authorization (unless your plan explicitly allows agency usage).</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">7. Subscription, Fees, and Taxes</h2>
            <p className="mb-3">
              If the Service is offered on a paid basis, you agree to pay fees described at the time of purchase or in an order form. Fees are non-refundable except where required by law or expressly stated otherwise. Taxes (including VAT) may apply based on your location and billing details.
            </p>
            <p>
              We may change pricing or introduce new fees with reasonable advance notice. Changes will apply prospectively.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">8. Service Availability and Changes</h2>
            <p className="mb-3">
              We aim to provide a reliable Service, but we do not guarantee uninterrupted availability. The Service may be unavailable due to maintenance, updates, outages, or factors outside our control (including third-party services like Meta, Google, Supabase, hosting providers, or internet service providers).
            </p>
            <p>
              We may modify, suspend, or discontinue all or part of the Service at any time. Where reasonable, we will provide advance notice of material changes.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">9. Beta Features</h2>
            <p>
              Some features may be labeled alpha, beta, preview, or similar. These features may be unstable and are provided "as is" and "as available." You use beta features at your own risk.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">10. Intellectual Property</h2>
            <p>
              The Service, including its software, design, and all related intellectual property rights, is owned by To Better Digital (TBD) and its licensors. Except for the limited rights expressly granted to you, no rights are granted by implication.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">11. Confidentiality</h2>
            <p>
              You may have access to non-public information about the Service. You agree not to disclose such information except as required to use the Service or as permitted by law.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">12. Suspension and Termination</h2>
            <p className="mb-2">
              You may stop using the Service at any time. We may suspend or terminate your access if we reasonably believe:
            </p>
            <ul className="mb-3 list-disc space-y-1 pl-6">
              <li>You violated these Terms or applicable laws or platform policies.</li>
              <li>Your use poses a security risk to the Service, other users, or third parties.</li>
              <li>Required by law or by a third-party provider's rules (e.g., Meta platform enforcement).</li>
              <li>Your account is overdue on payment (if applicable), after notice and a reasonable opportunity to cure.</li>
            </ul>
            <p>
              Upon termination, your right to use the Service stops. We may delete your data in accordance with our Privacy Policy and applicable law. You may request deletion by contacting{" "}
              <a href="mailto:jeremy@tobetterdigital.com" className="text-primary hover:underline">
                jeremy@tobetterdigital.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">13. Disclaimers</h2>
            <p className="mb-3">
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that the Service will be uninterrupted, error-free, or that it will produce specific advertising outcomes. Advertising performance depends on many factors outside our control, including creative, budgets, auction dynamics, and third-party platform changes.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">14. Limitation of Liability</h2>
            <p className="mb-3">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, TO BETTER DIGITAL (TBD) WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
            </p>
            <p className="mb-3">
              OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE SERVICE WILL NOT EXCEED THE AMOUNTS YOU PAID TO US FOR THE SERVICE IN THE TWELVE (12) MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR ONE HUNDRED EUROS (€100) IF YOU HAVE NOT PAID ANY AMOUNTS, WHICHEVER IS GREATER.
            </p>
            <p>
              Some jurisdictions do not allow certain limitations of liability, so these limitations may not apply to you to the extent prohibited by law.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">15. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless To Better Digital (TBD) from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or related to: (a) your Customer Content; (b) your use of the Service in violation of these Terms; or (c) your violation of any law or third-party rights, including Meta and Google platform policies.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">16. Third-Party Services</h2>
            <p>
              The Service integrates with third-party services (including Meta, Google Drive, Supabase, and hosting providers). We are not responsible for third-party services and do not control their actions. Your use of third-party services is governed by their terms and policies.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">17. Governing Law and Dispute Resolution</h2>
            <p className="mb-3">
              If you are located in the EEA/UK, these Terms are governed by the laws of the country where you are habitually resident, to the extent required by consumer protection laws; otherwise, these Terms are governed by the laws of France, without regard to conflict of law rules.
            </p>
            <p>
              Courts with competent jurisdiction will have exclusive jurisdiction over disputes arising from these Terms, except where mandatory law provides otherwise.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">18. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. We will post the updated Terms on this page and revise the "Last updated" date above. If changes are material, we may provide additional notice within the Service.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">19. Contact</h2>
            <p>
              Questions about these Terms can be sent to{" "}
              <a href="mailto:jeremy@tobetterdigital.com" className="text-primary hover:underline">
                jeremy@tobetterdigital.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}